import React, {useMemo} from "react";
import classnames from "classnames";
import Utils, {formatMoney, formatPhoneNumber} from "../../../../../core/services/utils/utils";
import DateUtils from "../../../../../core/services/date-utils";
import ElementIds from "../../../../../core/models/element-ids";


// 7 + 7 + 15 + 15 + 8 + 8

/**
 * Creates a product row given the item and its index.
 * @param item
 * @param index
 * @param length
 * @return {JSX.Element}
 */
const createItemRow = (item, index, length) => {
    return (
        <tr key={index} className={classnames('table-body side-borders', {'no-bottom-border': index !== length - 1})}>
            <td colSpan={7} className={'text-sm font-weight-semi-bold'}>
                {item?.date ? DateUtils.serverToLocal(item?.date).format(DateUtils.customFormats.customerOutstandingStatement) : ''}
            </td>
            <td colSpan={8} className={'text-sm font-weight-semi-bold'}>
                {item?.type ?? ''}
            </td>
            <td colSpan={15} className={'text-sm font-weight-semi-bold'}>
                {item?.referenceNo ?? ''}
            </td>
            <td colSpan={14} className={'text-sm font-weight-semi-bold'}>
                {item?.customerPO ?? ''}
            </td>
            <td colSpan={8} className={'text-right text-sm font-weight-semi-bold'}>
                {item?.debit ? formatMoney(item?.debit)?.replace('$', '') : (item?.credit ? formatMoney(item?.credit)?.replace('$', '') : '')}
            </td>
            <td colSpan={8} className={'text-right text-sm font-weight-semi-bold'}>
                {item?.balance ? formatMoney(item?.balance)?.replace('$', '') : ''}
            </td>
        </tr>
    );
}

// 9 + 9 + 9 + 9 +9 + 15

/**
 * Creates a Table Rooter Row.
 * @return {Element}
 */
const createTableFooterRow = ({
                                  key,
                                  className = '',
                                  values,
                                  tdClassName = '',
                                  ...props
                              }) => {
    return (
        <tr key={key} {...props} className={classnames('side-borders', className)}>
            <td colSpan={9} className={classnames('text-center font-weight-semi-bold text-md', tdClassName)}>
                {values[0] ?? ''}
            </td>
            <td colSpan={9} className={classnames('text-center font-weight-semi-bold text-md', tdClassName)}>
                {values[1] ?? ''}
            </td>
            <td colSpan={9} className={classnames('text-center font-weight-semi-bold text-md', tdClassName)}>
                {values[2] ?? ''}
            </td>
            <td colSpan={9} className={classnames('text-center font-weight-semi-bold text-md', tdClassName)}>
                {values[3] ?? ''}
            </td>
            <td colSpan={9} className={classnames('text-center font-weight-semi-bold text-md', tdClassName)}>
                {values[4] ?? ''}
            </td>
            <td colSpan={15} className={classnames('text-right font-weight-semi-bold text-md', tdClassName)}>
                {values[5] ?? ''}
            </td>
        </tr>
    );
}

const PrintCustomerOutstandingStatementTemplate = ({statement, partner}) => {
    const items = useMemo(() => statement?.items ?? [], [statement?.items]);

    return (
        <>
            <div id={ElementIds.printCustomerOutstandingStatement.page} className={'print-customer-outstanding-statement-template'}>
                <table id={ElementIds.printCustomerOutstandingStatement.table} width={'100%'} className={'table'}>
                    <colgroup>
                        {
                            Array(60).fill(1).map((e, index) => (
                                <col key={index} width="1.6666666666667%"/>
                            ))
                        }
                    </colgroup>
                    <tbody id={ElementIds.printCustomerOutstandingStatement.pageHeader}>
                    <tr className={''}>
                        <td colSpan={20} className={'no-border pr-4'}>
                            <img
                                src={Utils.encodeFileUrl(partner.logo)}
                                style={{objectFit: 'contain', height: 'fit-content', maxHeight: 120}}
                                className={'w-100'}
                                alt="logo"
                            />
                        </td>
                        <td colSpan={20} className={'no-border'}>
                            {/*PARTNER ADDRESS AND PHONE */}
                            <p className={'text-md font-weight-bold black line-height-2'}>
                                {partner.fullAddress}
                            </p>
                            <p className={'text-md font-weight-bold black mr-1 line-height-2'}>
                                {partner.city} {partner.province} {partner.postalCode}
                            </p>
                            <p className={'text-md font-weight-bold black mr-1 line-height-2'}>
                                Ph. <span>{partner.phone}</span>
                            </p>
                            <p className={'text-md font-weight-bold black mr-1 line-height-2'}>
                                Fx. <span>{partner.fax}</span>
                            </p>
                            <p className={'text-md font-weight-bold black mr-1 line-height-2'}>
                                W: <span> {Utils.formatLinkUrlDisplay(partner.website) ?? ''}</span>
                            </p>
                        </td>
                        <td colSpan={20} className={'no-border'}>
                            {/* Statement Details */}
                            <p className={'title-text black font-weight-bold mb-2'}>
                                Statement
                            </p>
                            <p className={'text-md'}>
                                <span className={'font-weight-bold'} style={{paddingRight: 100}}>
                                    Date
                                </span>
                                <span
                                    className={'font-weight-semi-bold text-sm'}>
                                    {DateUtils.localNow().format(DateUtils.dateFormatAlternative)}
                                </span>
                            </p>
                            <p className={'text-md'}>
                                <span className={'font-weight-bold'} style={{paddingRight: 63}}>
                                    Customer
                                </span>
                                <span
                                    className={'font-weight-semi-bold text-sm'}>
                                    {statement?.customer?.code ?? ''}
                                </span>
                            </p>
                        </td>
                    </tr>
                    <tr className={''}>
                        <td colSpan={45} className={'no-border'} height={'50px'}/>
                        <td colSpan={15} className={'no-border'} height={'50px'}>
                            <p className={'page-identifier'}>
                                Page 0 of 0
                            </p>
                        </td>
                    </tr>
                    <tr className={''}>
                        <td colSpan={60} className={'no-border pt-4'}>
                            {/*BILL-TO ADDRESS*/}
                            <p className={'text-md font-weight-bold mr-2'}>
                                <span style={{paddingRight: 30}}>Bill To:</span>
                                <span className={'text-lg'}>{statement?.customer?.name ?? ''}</span>
                            </p>
                            <p className={'text-md font-weight-semi-bold'}>
                                <span style={{paddingLeft: 82}}>
                                    {statement?.customer?.address ?? ''}
                                </span>
                            </p>
                            <p className={'text-md font-weight-semi-bold pb-4'}>
                                <span style={{paddingLeft: 82}}>
                                    {statement?.customer?.city ?? ''}
                                </span>
                                <span className={'ml-2'}>
                                    {statement?.customer?.province ?? ''}
                                </span>
                                <span className={'ml-2'}>
                                    {statement?.customer?.postalCode ?? ''}
                                </span>
                            </p>
                            <p className={'text-md font-weight-semi-bold black'}>
                                <span style={{paddingLeft: 82}}>
                                    {
                                        statement?.customer?.phone &&
                                        `Ph. ${formatPhoneNumber(statement?.customer?.phone)}`
                                    }
                                </span>
                            </p>
                        </td>
                    </tr>
                    <tr className={''}>
                        <td className={'no-border'} colSpan={60} height={'20px'}/>
                    </tr>
                    </tbody>
                    <tbody id={ElementIds.printCustomerOutstandingStatement.productsTable.header}>
                    {/*First ROW OF TABLE*/}
                    <tr className={'side-borders'}>
                        <th colSpan={7} className={'text-left text-md align-bottom'}>
                            Date
                        </th>
                        <th colSpan={8} className={'text-left text-md align-bottom'}>
                            Description
                        </th>
                        <th colSpan={15} className={'text-left text-md align-bottom'}>
                            Reference
                        </th>
                        <th colSpan={14} className={'text-left text-md align-bottom'}>
                            PO#
                        </th>
                        <th colSpan={8} className={'text-right text-md align-bottom'}>
                            Amount
                        </th>
                        <th colSpan={8} className={'text-right text-md align-bottom'}>
                            Outstanding
                        </th>
                    </tr>
                    </tbody>
                    <tbody id={ElementIds.printCustomerOutstandingStatement.productsTable.body} className={'table-border no-bottom-border spacer-borders'}>
                    {
                        items.map(createItemRow)
                    }
                    {/*BOTTOM ROW OF TABLE BODY*/}
                    <tr id={ElementIds.printCustomerOutstandingStatement.productsTable.bodyFiller} className={classnames('no-top-border side-borders')}>
                        <td colSpan={7} className={classnames('py-2-5 text-left font-weight-semi-bold text-md')}/>
                        <td colSpan={8} className={classnames('py-2-5 text-left font-weight-semi-bold text-md')}/>
                        <td colSpan={15} className={classnames('py-2-5 text-left font-weight-semi-bold text-md')}/>
                        <td colSpan={14} className={classnames('py-2-5 text-left font-weight-semi-bold text-md')}/>
                        <td colSpan={8} className={classnames('py-2-5 text-right font-weight-semi-bold text-md')}/>
                        <td colSpan={8} className={classnames('py-2-5 text-right font-weight-semi-bold text-md')}/>
                    </tr>
                    <tr className={classnames('no-bottom-border no-top-border no-side-border')}>
                        <td colSpan={7} className={classnames('py-2-5 no-border')}/>
                        <td colSpan={8} className={classnames('py-2-5 no-border')}/>
                        <td colSpan={15} className={classnames('py-2-5 no-border')}/>
                        <td colSpan={14} className={classnames('py-2-5 no-border')}/>
                        <td colSpan={8} className={classnames('py-2-5 no-border')}/>
                        <td colSpan={8} className={classnames('py-2-5 no-border')}/>
                    </tr>
                    </tbody>
                    <tbody id={ElementIds.printCustomerOutstandingStatement.productsTable.footerContinue} className={'table-footer-border'}>
                    <tr id={ElementIds.printCustomerOutstandingStatement.productsTable.bodyFiller} className={classnames('no-bottom-border no-top-border side-borders')}>
                        <td colSpan={7} className={classnames('py-2-5 text-left font-weight-semi-bold text-md no-border')}/>
                        <td colSpan={8} className={classnames('py-2-5 text-left font-weight-semi-bold text-md no-border')}/>
                        <td colSpan={15} className={classnames('py-2-5 text-left font-weight-semi-bold text-md no-border')}/>
                        <td colSpan={14} className={classnames('py-2-5 text-left font-weight-semi-bold text-md no-border')}/>
                        <td colSpan={16} className={classnames('py-2-5 text-right font-weight-semi-bold text-md no-border')}>
                            Continued...
                        </td>
                    </tr>
                    </tbody>
                    <tbody id={ElementIds.printCustomerOutstandingStatement.productsTable.footer} className={'table-footer-border'}>
                    {createTableFooterRow({
                        className: 'no-bottom-border no-top-border',
                        tdClassName: 'font-weight-bold',
                        values: [
                            'Current',
                            'Over 30',
                            'Over 60',
                            'Over 90',
                            'Over 120',
                            'Total Due'
                        ]
                    })}
                    {createTableFooterRow({
                        className: 'no-bottom-border no-top-border',
                        values: [
                            formatMoney(statement?.ar?.current ?? 0),
                            formatMoney(statement?.ar?.over30 ?? 0),
                            formatMoney(statement?.ar?.over60 ?? 0),
                            formatMoney(statement?.ar?.over90 ?? 0),
                            formatMoney(statement?.ar?.over120 ?? 0),
                            formatMoney(statement?.ar?.balance ?? 0)
                        ]
                    })}
                    </tbody>
                </table>
            </div>
        </>
    );
}

export default PrintCustomerOutstandingStatementTemplate;


